// Sizing for the Releases pixelation/mosaic overlay.
//
// The mosaic tile is sized to the banner's TITLE font: at coefficient 1 a tile
// is one title-cap square. The size is read from each template's own `.title`
// element, so the base and @2x variants scale automatically — Splash's 60px
// title → 60px tiles on its 640px canvas; Splash2x's 120px title → 120px tiles
// on its 1280px canvas (the same mosaic at 2×). Tile COUNTS therefore differ
// from template to template, which is intended: the size, not the count, is what
// tracks the design.
//
// The size is a single scalar, so it is independent of direction (rotating only
// changes which axis fades) and of coverage (the Coverage slider only changes
// how many rows are drawn, never their size — see libs/pixelation.drawPixelation).

/** Minimum drawable square edge, px. */
const MIN_SQUARE = 6;

/** Title font size (px) assumed when a template has no readable `.title`. */
const FALLBACK_TITLE_PX = 80;

/**
 * Read the title font size (px) from a template element, preferring the full
 * title over a `.title--small` variant. Returns FALLBACK_TITLE_PX when the
 * element, the `.title` node, or its computed size can't be resolved. Reads
 * layout px via getComputedStyle — the same coordinate space as the canvas
 * backing store (offsetWidth) — so it stays consistent across preview zoom and
 * the @2x variants.
 */
export function getTitleFontSizeFromTemplate(templateElement: Element | null): number {
  if (!templateElement) return FALLBACK_TITLE_PX;
  const titleEl =
    templateElement.querySelector(".title:not(.title--small)") ||
    templateElement.querySelector(".title");
  if (!titleEl) return FALLBACK_TITLE_PX;

  const fontSize = window.getComputedStyle(titleEl).fontSize;
  const match = fontSize.match(/^(\d+(?:\.\d+)?)/);
  if (!match) return FALLBACK_TITLE_PX;
  const value = parseFloat(match[1]);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : FALLBACK_TITLE_PX;
}

/**
 * The mosaic tile edge (px): the title font size scaled by the user's
 * coefficient (1 = one title-cap square). Floored so tiles stay drawable.
 */
export function titleSquareSize(titleFontSize: number, coefficient: number): number {
  return Math.max(MIN_SQUARE, Math.floor(titleFontSize * coefficient));
}

/**
 * The full-span (cross-axis) tile edge (px), fitted so a whole number of tiles
 * spans `crossExtent` exactly — the last row/column reaches the edge instead of
 * overrunning it and being clipped. `size` is the fade-axis edge; the divisor is
 * the SAME tile count a naive square layout would use (`ceil(crossExtent / size)`,
 * mirroring drawPixelation's `ceil`), so the cross-axis cell count is unchanged
 * and per-cell `row:col` overrides keep landing on the same ordinal squares —
 * the tiles only shrink (result ≤ size) to absorb the sliver that would
 * otherwise be clipped. Returns `size` unchanged for non-positive inputs (square
 * cells) so drawPixelation's own default still applies.
 */
export function fitCrossSize(crossExtent: number, size: number): number {
  if (!(crossExtent > 0) || !(size > 0)) return size;
  const count = Math.max(1, Math.ceil(crossExtent / size));
  return crossExtent / count;
}
